import React, { useState } from "react";
import { Box, Container, Snackbar, Alert, Typography, Card, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import ImportExcel from "./ImportExcel";
import ImportedCutoffs from "./components/ImportedCutoffs";
import DataExport from "./components/DataExport";
import { useResponsive } from "../../utils/mobileresponsive";
import UpdateProfile from "./components/AgentSettings";
import AuditLog from "./components/AdminAuditLog";
import AgentPreferences from "./components/AgentPreferences/AgentPreferences";
import AgentCredentialVault from "../AgentManagement/components/CredentialVault/AgentCredentialVault";
import NotificationsCenter from "./components/NotificationsCenter";
import UsersMFA from "./components/UsersMFA/UsersMFA";
import FloatingSupportButton from "../../components/FloatingSupportButton";
import { isFieldAgent } from "../../utils/roleDetection";

const baseApiUrl = `${window.location.origin}/wp-json/ahg/v1`;
const primaryBlue = "#1D54A2";

export default function Settings() {
	const [activeTab, setActiveTab] = useState(0);
	// Bumped after a successful import so the imported-cutoff list refetches.
	const [importedCutoffsKey, setImportedCutoffsKey] = useState(0);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMsg, setSnackbarMsg] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState("info");
	const { isMobile } = useResponsive();

	const stored = localStorage.getItem("loggedin_data");
	const user = stored ? JSON.parse(stored) : null;
	const roles = Array.isArray(user?.roles) ? user.roles : [];
	const isAgent = isFieldAgent(roles);

	// Agent's profile data now comes straight from the logged-in user record
	const profileData = isAgent ? user : null;

	const handleStartLoading = () => {};
	const handleStopLoading = () => {};

	const showSnackbar = (msg, severity = "info") => {
		setSnackbarMsg(msg);
		setSnackbarSeverity(severity);
		setSnackbarOpen(true);
	};

	const handleSnackbarClose = () => setSnackbarOpen(false);

	const tabs = isAgent
		? [
				{ label: "Profile", icon: PersonOutlinedIcon },
				{ label: "Preferences", icon: NotificationsNoneOutlinedIcon },
				{ label: "Credential Vault", icon: VpnKeyOutlinedIcon },
			]
		: [
				{ label: "Data Import Uploads", icon: UploadFileOutlinedIcon },
				{ label: "Data Export", icon: FileDownloadOutlinedIcon },
				{ label: "Audit Logs", icon: HistoryIcon },
				{ label: "Agent Preferences", icon: TuneIcon },
				{ label: "Notifications", icon: NotificationsActiveOutlinedIcon },
				{ label: "MFA Preferences", icon: SecurityOutlinedIcon },
			];

	return (
		<Container maxWidth={false} sx={{ width: "100%", mt: 7, mb: 10 }}>
			<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: primaryBlue, letterSpacing: 4, mb: 3 }}>
				{isAgent ? "PROFILE & PREFERENCES" : "SETTINGS"}
			</Typography>

			<Card sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, minHeight: 480, width: "100% !important" }}>
				<List
					component="nav"
					sx={{
						borderRight: { xs: 0, md: 1 },
						borderBottom: { xs: 1, md: 0 },
						borderColor: { xs: "divider", md: "divider" },
						width: { xs: "100%", md: "auto" },
						minWidth: { xs: "auto", md: 240 },
						flexShrink: 0,
						p: 2,
					}}
				>
					{tabs.map((tab, index) => {
						const TabIcon = tab.icon;

						return (
							<ListItemButton
								key={tab.label}
								selected={activeTab === index}
								onClick={() => setActiveTab(index)}
								sx={{
									borderRadius: 1.5,
									py: 0.75,
									mb: 0.25,
									color: "#24292F",
									borderLeft: "3px solid transparent",
									"&:hover": {
										bgcolor: "#EBF5FF",
									},
									"&:focus-visible": {
										outline: `2px solid ${primaryBlue}`,
										outlineOffset: 1,
									},
									"&.Mui-selected": {
										bgcolor: "#EBF5FF",
										borderLeft: `3px solid ${primaryBlue}`,
										"&:hover": {
											bgcolor: "#EBF5FF",
										},
									},
								}}
							>
								<ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
									<TabIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText
									primary={tab.label}
									primaryTypographyProps={{
										fontSize: "0.875rem",
										fontWeight: activeTab === index ? 600 : 400,
									}}
								/>
							</ListItemButton>
						);
					})}
				</List>

				<Box sx={{ flex: 1, p: { xs: 2, sm: 3, md: 4 }, minWidth: 0 }}>
					{isAgent ? (
						activeTab === 2 ? (
							<AgentCredentialVault />
						) : (
							<UpdateProfile profileData={profileData} section={activeTab === 0 ? "profile" : "preferences"} />
						)
					) : activeTab === 0 ? (
						<Box sx={{ flex: 1, width: "100% !important" }}>
							<ImportExcel
								headline="Agent's BoB from HS"
								apiUrl={`${baseApiUrl}/import-bob`}
								handleStartLoading={handleStartLoading}
								handleStopLoading={handleStopLoading}
								showSnackbar={showSnackbar}
								confirmMessage="Are you sure you want to import this file?"
								onImported={() => setImportedCutoffsKey((k) => k + 1)}
							/>

							<ImportedCutoffs showSnackbar={showSnackbar} refreshKey={importedCutoffsKey} />
						</Box>
					) : activeTab === 1 ? (
						<DataExport showSnackbar={showSnackbar} />
					) : activeTab === 2 ? (
						<AuditLog />
					) : activeTab === 3 ? (
						<AgentPreferences showSnackbar={showSnackbar} />
					) : activeTab === 4 ? (
						<NotificationsCenter />
					) : (
						<UsersMFA showSnackbar={showSnackbar} />
					)}
				</Box>
			</Card>

			<Snackbar
				open={snackbarOpen}
				autoHideDuration={6000}
				onClose={handleSnackbarClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleSnackbarClose}
					severity={snackbarSeverity}
					sx={{
						width: "100%",
						"& .MuiAlert-action button": { backgroundColor: "transparent" },
					}}
				>
					{snackbarMsg}
				</Alert>
			</Snackbar>
			{isAgent && <FloatingSupportButton />}
		</Container>
	);
}
